import { useMemo } from 'react';
import { useLatest } from '../../hooks/useLatest';
import { localDateISO } from '../../logic/dates';
import { listDayOf } from '../../logic/openList';
import { suggestCategory, planRetarget } from '../../logic/categorize';
import { CLOSING_SEED } from '../../config/closingSeed';
import {
  CLOSING_SECTIONS_COLLECTION, CLOSING_SUBSECTIONS_COLLECTION, CLOSING_ITEMS_COLLECTION,
} from '../../config/constants';
import { addItem, removeItem, restoreItem, updateItem } from '../../data/items';
import { saveFailedNotifier } from '../../data/errors';
import {
  addClosingSection, updateClosingSection, removeClosingSection,
  addClosingSubsection, updateClosingSubsection, removeClosingSubsection,
  addClosingItem, updateClosingItem, removeClosingItems, restoreClosingItems, swapOrder, resetClosing,
  applyClosingTargets,
} from '../../data/closing';

const nextOrder = arr => arr.reduce((m, x) => Math.max(m, x.order || 0), 0) + 10;

/**
 * Everything the Closing tab writes, as ONE object that never changes identity — it reads
 * the latest data through a ref — so the memoized item rows don't re-render whenever the
 * board does. `state` (refreshed every render):
 *   sections, subsections, items                 the template
 *   orderedSections, subsBySection, itemsBySub   …in walk order
 *   secById, subById, itemById                   lookups
 *   onBoard(name, target)                        the board row a tick made (if any)
 *   newCycle                                     a new list cycle began, list not started yet
 *   lists, route, author, notify, t, ln, short, withList, selected, clearSelection
 * A refused write toasts why (data/errors.js); Firestore has already rolled it back.
 */
export function useClosingActions(state) {
  const latest = useLatest(state);

  return useMemo(() => {
    const now = () => latest.current;
    const failed = err => { const { notify, t } = now(); saveFailedNotifier(notify, t)(err); };

    // Each item's effective list (item → sub-section → section), optionally with pending
    // changes applied: { sec, sub, item } are Map<id, target>, `itemSub` Map<itemId, subsectionId>.
    const resolveAll = (o = {}) => {
      const { items, subById, secById } = now();
      const pick = (m, id, fallback) => (m && m.has(id) ? m.get(id) : fallback);
      const out = new Map();
      items.forEach(i => {
        const su = subById.get(pick(o.itemSub, i.id, i.subsectionId));
        const sec = su && secById.get(su.sectionId);
        const secT = sec ? pick(o.sec, sec.id, sec.target) : null;
        const subT = su ? pick(o.sub, su.id, su.target || null) : null;
        out.set(i.id, pick(o.item, i.id, i.target || null) || subT || secT);
      });
      return out;
    };

    // Commit list changes at any level in one batch — and items moved to another
    // sub-section (`subsectionId` / `order` in an item patch). Today's board rows of every
    // item whose effective list changes move with it (the tick stays, the list stays right).
    // `message(boardMoved)` → toast text (null = none); the toast offers undo.
    const commitTargets = ({ sections: secP = [], subsections: subP = [], items: itemP = [] }, message) => {
      const { items, secById, subById, itemById, onBoard, route, notify } = now();
      const o = {
        sec: new Map(secP.map(p => [p.id, p.target])),
        sub: new Map(subP.map(p => [p.id, p.target])),
        item: new Map(itemP.filter(p => 'target' in p).map(p => [p.id, p.target])),
        itemSub: new Map(itemP.filter(p => 'subsectionId' in p).map(p => [p.id, p.subsectionId])),
      };
      const before = resolveAll();
      const after = resolveAll(o);
      const board = [];
      items.forEach(i => {
        const from = before.get(i.id); const to = after.get(i.id);
        const row = from !== to && onBoard(i.name, from);
        const dest = route(i.name, to);
        if (row && row.category !== dest && !onBoard(i.name, to)) board.push({ id: row.id, category: dest, from: row.category });
      });
      const undo = {
        sections: secP.map(p => ({ id: p.id, target: secById.get(p.id).target })),
        subsections: subP.map(p => ({ id: p.id, target: subById.get(p.id).target || null })),
        items: itemP.map(({ id, ...patch }) => {
          const it = itemById.get(id);
          const prev = { id };
          if ('target' in patch) prev.target = it.target || null;
          if ('keepList' in patch) prev.keepList = !!it.keepList;
          if ('subsectionId' in patch) prev.subsectionId = it.subsectionId;
          if ('order' in patch) prev.order = it.order || 0;
          return prev;
        }),
        board: board.map(b => ({ id: b.id, category: b.from })),
      };
      applyClosingTargets({ sections: secP, subsections: subP, items: itemP, board: board.map(({ id, category }) => ({ id, category })) }).catch(failed);
      const text = message && message(board.length);
      if (text) notify(text, () => applyClosingTargets(undo).catch(failed));
    };
    const boardNote = n => (n ? now().t('sort.boardMoved', { n }) : null);

    // Load the default sheets — atomically replaces whatever's there (an empty scaffold or
    // a mangled list becomes the full seeded tree in one tap).
    const loadDefaults = () => {
      const { sections, subsections, items, author } = now();
      resetClosing(CLOSING_SEED, {
        sectionIds: sections.map(s => s.id), subsectionIds: subsections.map(s => s.id), itemIds: items.map(i => i.id),
      }, author).catch(failed);
    };

    return {
      // Tap an item → add it to (or take it off) the Today board, in the list its name
      // lives in. Adding goes through the open list's gate; in a new cycle an item still
      // on the old list is re-noted for tonight (never deleted).
      toggle(item, sub, section) {
        const { onBoard, route, newCycle, withList, notify, t, ln, short, author } = now();
        const base = item.target || sub.target || section.target;
        const target = route(item.name, base);
        const existing = onBoard(item.name, base);
        if (existing && newCycle) {
          withList((listFor, { archived }) => {
            if (!archived.has(existing.id)) {
              if (listFor && listDayOf(existing) !== listFor) updateItem(existing.id, { listFor }).catch(failed);
            } else {
              addItem({ date: localDateISO(), listFor, category: target, name: item.name, qty: '', addedBy: author }).saved.catch(failed);
            }
            notify(t('closing.added', { name: ln(item.name), cat: short(target) }));
          }, item.name);
          return;
        }
        if (existing) {
          removeItem(existing.id).catch(failed);
          notify(t('closing.removed', { name: ln(item.name) }), () => restoreItem(existing).catch(failed));
          return;
        }
        withList(listFor => {
          const added = addItem({ date: localDateISO(), listFor, category: target, name: item.name, qty: '', addedBy: author });
          added.saved.catch(failed);
          notify(t('closing.added', { name: ln(item.name), cat: short(target) }), () => removeItem(added.id).catch(failed));
        }, item.name);
      },

      // ── List changes ─────────────────────────────────────────────────────────
      setSectionTarget: (sec, v) => commitTargets({ sections: [{ id: sec.id, target: v }] }, boardNote),
      setSubTarget: (su, v) => commitTargets({ subsections: [{ id: su.id, target: v }] }, boardNote),
      // Hand-picking an item's list is a decision: remember it (`keepList`) unless it's just
      // what the name suggests anyway. Matching the sub-section's list stores no override.
      setItemTarget(it, subTarget, v) {
        const s = suggestCategory(it.name, it);
        commitTargets({ items: [{ id: it.id, target: v === subTarget ? null : v, keepList: !(s && s.id === v) }] }, boardNote);
      },
      // A rename re-opens the question if the new name points somewhere else.
      rename(it, name) {
        const patch = { name };
        const was = suggestCategory(it.name, it); const next = suggestCategory(name, it);
        if (it.keepList && (was && was.id) !== (next && next.id)) patch.keepList = false;
        updateClosingItem(it.id, patch).catch(failed);
      },
      // "✨ Sort into lists" → moves: Map<itemId, list>; keep: Map<itemId, bool> (hand-picked /
      // declined suggestions). A tidy plan: each touched sub-section takes the list most of its
      // items use, only the odd ones out keep an override.
      applySort({ moves, keep }) {
        const { sections, subsections, items, itemById, lists, t } = now();
        const plan = planRetarget({ sections, subsections, items, finals: moves, order: lists.active.map(c => c.id) });
        const patches = new Map(plan.items.map(p => [p.id, { target: p.target }]));
        keep.forEach((v, id) => {
          const it = itemById.get(id);
          if (it && !!it.keepList !== v) patches.set(id, { ...(patches.get(id) || {}), keepList: v });
        });
        if (!plan.subsections.length && !patches.size) return;
        commitTargets(
          { subsections: plan.subsections, items: [...patches].map(([id, p]) => ({ id, ...p })) },
          () => (moves.size ? t('sort.applied', { n: moves.size }) : t('sort.keptN', { n: keep.size })),
        );
      },

      // ── Edit the tree ────────────────────────────────────────────────────────
      loadDefaults,
      resetToDefaults() { if (window.confirm(now().t('closing.resetConfirm'))) loadDefaults(); },
      renameSection: (sec, title) => updateClosingSection(sec.id, { title }).catch(failed),
      renameSub: (su, title) => updateClosingSubsection(su.id, { title }).catch(failed),
      addSection() {
        const { orderedSections, author, t } = now();
        addClosingSection({ title: t('closing.newSection'), target: 'cooking', order: nextOrder(orderedSections), by: author }).catch(failed);
      },
      addSub(sec) {
        const { subsBySection, author, t } = now();
        addClosingSubsection({ sectionId: sec.id, title: t('closing.newSubsection'), target: sec.target, order: nextOrder(subsBySection.get(sec.id) || []), by: author }).catch(failed);
      },
      // New items land in the list their name suggests (an override only when it differs
      // from the sub-section's), with a toast saying where.
      addItemTo(sub, subTarget, name) {
        const { itemsBySub, author, notify, t, short } = now();
        const s = suggestCategory(name);
        const target = s && s.id !== subTarget ? s.id : null;
        addClosingItem({ subsectionId: sub.id, name, target, order: nextOrder(itemsBySub.get(sub.id) || []), by: author }).catch(failed);
        if (target) notify(t('sort.autoPlaced', { name, cat: short(target), word: s.match }));
      },
      deleteSection(sec) {
        const { subsBySection, itemsBySub, t, ln } = now();
        const subs = subsBySection.get(sec.id) || [];
        const itemIds = subs.flatMap(su => (itemsBySub.get(su.id) || []).map(i => i.id));
        if (window.confirm(t('closing.deleteSectionConfirm', { title: ln(sec.title) }))) removeClosingSection(sec.id, subs.map(s => s.id), itemIds).catch(failed);
      },
      deleteSub(sub) {
        const { itemsBySub, t, ln } = now();
        const itemIds = (itemsBySub.get(sub.id) || []).map(i => i.id);
        if (window.confirm(t('closing.deleteSubConfirm', { title: ln(sub.title) }))) removeClosingSubsection(sub.id, itemIds).catch(failed);
      },
      // Swap with the neighbour above (-1) / below (+1).
      moveSection(idx, dir) { const list = now().orderedSections; const a = list[idx]; const b = list[idx + dir]; if (a && b) swapOrder(CLOSING_SECTIONS_COLLECTION, a, b).catch(failed); },
      moveSub(list, idx, dir) { const a = list[idx]; const b = list[idx + dir]; if (a && b) swapOrder(CLOSING_SUBSECTIONS_COLLECTION, a, b).catch(failed); },
      moveItem(list, idx, dir) { const a = list[idx]; const b = list[idx + dir]; if (a && b) swapOrder(CLOSING_ITEMS_COLLECTION, a, b).catch(failed); },
      // Multi-select delete (items), undo-able.
      deleteSelected() {
        const { items, selected, clearSelection, notify, t } = now();
        const rows = items.filter(i => selected.has(i.id));
        if (!rows.length || !window.confirm(t('closing.deleteSelectedConfirm', { n: rows.length }))) return;
        removeClosingItems(rows.map(r => r.id)).catch(failed);
        clearSelection();
        notify(t('closing.deletedN', { n: rows.length }), () => restoreClosingItems(rows).catch(failed));
      },
      // Multi-select "Move to…": the picked items go to the end of sub-section `toSubId`, in
      // walk order. A hand-picked list stays — unless it's now just the new sub-section's
      // list — and today's board rows follow any list change. One batch, undo-able.
      moveSelected(toSubId) {
        const { orderedSections, subsBySection, itemsBySub, subById, secById, selected, clearSelection, t, ln } = now();
        const dest = subById.get(toSubId);
        if (!dest) return;
        const destSec = secById.get(dest.sectionId);
        const destTarget = dest.target || (destSec && destSec.target) || null;
        const rows = orderedSections
          .flatMap(sec => (subsBySection.get(sec.id) || []).flatMap(su => itemsBySub.get(su.id) || []))
          .filter(i => selected.has(i.id) && i.subsectionId !== toSubId);
        clearSelection();
        if (!rows.length) return;
        const start = nextOrder(itemsBySub.get(toSubId) || []);
        const patches = rows.map((it, i) => {
          const p = { id: it.id, subsectionId: toSubId, order: start + i * 10 };
          if (it.target && it.target === destTarget) p.target = null;
          return p;
        });
        commitTargets({ items: patches }, boardMoved => {
          const text = t('closing.movedN', { n: rows.length, title: ln(dest.title) });
          return boardMoved ? `${text} · ${t('sort.boardMoved', { n: boardMoved })}` : text;
        });
      },
    };
  }, [latest]);
}
